import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import AppLayout from '@/app/layouts/AppLayout.vue'
import { useSessionStore } from '@/app/stores/session'
import { getAccessToken, refreshAccessToken } from '@/shared/api/http'
import { installRouteLoadRecovery } from './route-load-recovery'

const allResourceRoutes: Array<[string, string]> = [
  ['nodes', '服务节点配置'],
  ['templates', '汇总模板管理'],
  ['meds', '用药提醒'],
  ['revenue', '六大收益口'],
  ['overseas', '海外转介'],
  ['orgs', '医院机构管理'],
  ['banners', 'Banner 管理'],
  ['articles', '健康资讯'],
  ['audit', '操作日志'],
]

const productionResourceKeys = new Set(['meds', 'audit'])

const resourcePermissions: Record<string, string> = {
  meds: 'medication:read',
  audit: 'audit:read',
}

const resourceRoutes: RouteRecordRaw[] = allResourceRoutes
  .filter(([resource]) => productionResourceKeys.has(resource))
  .map(([resource, title]) => ({
    path: resource,
    name: resource,
    component: () => import('@/features/resources/pages/ResourcePage.vue'),
    meta: { title, group: title, resource, requiresAuth: true, permission: resourcePermissions[resource] },
  }))

const children: RouteRecordRaw[] = [
  { path: '', redirect: '/patients' },
  {
    path: 'patients',
    name: 'patients',
    component: () => import('@/features/patients/pages/PatientListPage.vue'),
    meta: { title: '患者列表', group: '患者管理', requiresAuth: true, permission: 'patient:read' },
  },
  {
    path: 'patients/:id',
    name: 'patient-detail',
    component: () => import('@/features/patients/pages/PatientDetailPage.vue'),
    meta: { title: '患者详情', group: '患者管理', requiresAuth: true, permission: 'patient:read' },
  },
  {
    path: 'reports',
    name: 'reports',
    component: () => import('@/features/report-center/pages/ReportPatientListPage.vue'),
    meta: { title: '报告审核', group: '患者管理', requiresAuth: true, permission: 'report:recognize' },
  },
  {
    path: 'report-files',
    name: 'report-files',
    component: () => import('@/features/report-center/pages/ReportPatientManagementListPage.vue'),
    meta: { title: '全部报告', group: '患者管理', requiresAuth: true, permission: 'report:read' },
  },
  {
    path: 'reports/patients/:patientId',
    name: 'patient-report-center',
    component: () => import('@/features/report-center/pages/PatientReportCenterPage.vue'),
    meta: { title: '报告中心', group: '患者管理', requiresAuth: true, permission: 'report:read' },
  },
  {
    path: 'reports/patients/:patientId/reports/:reportId/review',
    name: 'report-ocr-review',
    component: () => import('@/features/report-center/pages/ReportOCRReviewPage.vue'),
    meta: { title: '报告详情', group: '患者管理', requiresAuth: true, permission: 'report:recognize' },
  },
  {
    path: 'reports/patients/:patientId/reports/:reportId/advice/:sectionId',
    name: 'report-advice',
    component: () => import('@/features/report-center/pages/ReportAdvicePage.vue'),
    meta: { title: 'AI 结论与方案', group: '患者管理', requiresAuth: true, permission: 'report:read' },
  },
  {
    path: 'attribution',
    name: 'attribution',
    component: () => import('@/features/attribution/pages/AttributionPage.vue'),
    meta: { title: '八维归因评分规则', group: '八维归因配置', requiresAuth: true, permission: 'attribution:read' },
  },
  {
    path: 'assessment-indicators',
    name: 'assessment-indicators',
    component: () => import('@/features/attribution/pages/AssessmentIndicatorsPage.vue'),
    meta: { title: '八维归因指标配置', group: '八维归因配置', requiresAuth: true, permission: 'attribution:read' },
  },
  {
    path: 'ivf-phases',
    name: 'ivf-phases',
    component: () => import('@/features/attribution/pages/IVFPhaseCatalogPage.vue'),
    meta: { title: '试管阶段配置', group: '八维归因配置', requiresAuth: true, permission: 'attribution:read' },
  },
  {
    path: 'daily-checkin',
    name: 'daily-checkin',
    component: () => import('@/features/checkin/pages/DailyCheckinPage.vue'),
    meta: { title: '每日打卡', group: '内容运营', requiresAuth: true, permission: 'checkin:read' },
  },
  {
    path: 'daily-checkin/detail/:patientId',
    name: 'daily-checkin-detail',
    component: () => import('@/features/checkin/pages/DailyCheckinDetailPage.vue'),
    meta: { title: '打卡详情', group: '内容运营', requiresAuth: true, permission: 'checkin:read' },
  },
  {
    path: 'daily-checkin/template',
    name: 'daily-checkin-template-legacy',
    redirect: { name: 'daily-checkin-template-editor' },
  },
  {
    path: 'consultations',
    name: 'consultations',
    component: () => import('@/features/consultations/pages/ConsultationManagementPage.vue'),
    meta: { title: '咨询管理', group: '内容运营', requiresAuth: true, permission: 'consultation:read' },
  },
  {
    path: 'questionnaire-center',
    name: 'questionnaire-center',
    component: () => import('@/features/questionnaire/pages/QuestionnaireCenterPage.vue'),
    meta: { title: '问卷中心', group: '内容运营', requiresAuth: true, permission: 'questionnaire:read' },
  },
  {
    path: 'questionnaire-center/:patientId',
    name: 'questionnaire-center-detail',
    component: () => import('@/features/questionnaire/pages/QuestionnaireCenterDetailPage.vue'),
    meta: { title: '问卷详情', group: '内容运营', requiresAuth: true, permission: 'questionnaire:read' },
  },
  {
    path: 'questionnaires',
    name: 'questionnaires',
    component: () => import('@/features/questionnaire/pages/QuestionnaireTemplateListPage.vue'),
    meta: {
      title: '模板管理',
      group: '内容运营',
      requiresAuth: true,
      permissionsAny: ['questionnaire:read', 'checkin:read'],
    },
  },
  {
    path: 'questionnaires/daily-checkin',
    name: 'daily-checkin-template-editor',
    component: () => import('@/features/checkin/pages/DailyCheckinTemplatePage.vue'),
    meta: { title: '每日打卡模板编辑', group: '模板管理', requiresAuth: true, permission: 'checkin:write' },
  },
  {
    path: 'questionnaires/templates/:templateId',
    name: 'questionnaire-template-editor',
    component: () => import('@/features/questionnaire/pages/QuestionnaireTemplatePage.vue'),
    meta: { title: '问卷模板编辑', group: '模板管理', requiresAuth: true, permission: 'questionnaire:write' },
  },
  {
    path: 'accounts',
    name: 'accounts',
    component: () => import('@/features/identity/pages/AccountManagementPage.vue'),
    meta: { title: '用户管理', group: '角色权限', requiresAuth: true, permission: 'identity:account-read' },
  },
  {
    path: 'roles',
    name: 'roles',
    component: () => import('@/features/identity/pages/RoleManagementPage.vue'),
    meta: { title: '角色管理', group: '角色权限', requiresAuth: true, permission: 'identity:role-read' },
  },
  {
    path: 'operations/third-party',
    name: 'third-party-operations',
    component: () => import('@/features/operations/pages/ThirdPartyOperationsPage.vue'),
    meta: { title: '第三方服务', group: '系统运维', requiresAuth: true, permission: 'operations:third-party-read' },
  },
  {
    path: 'forbidden',
    name: 'forbidden',
    component: () => import('@/shared/pages/ForbiddenPage.vue'),
    meta: { title: '无访问权限', group: '管理后台', requiresAuth: true },
  },
  ...resourceRoutes,
]

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/features/identity/pages/LoginPage.vue'),
    meta: { title: '登录' },
  },
  {
    path: '/doctor-review/:shareId',
    name: 'doctor-review',
    component: () => import('@/features/doctor-review/pages/DoctorReviewPage.vue'),
    meta: { title: '医生协作工作台' },
  },
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    children,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/shared/pages/NotFoundPage.vue'),
    meta: { title: '页面不存在' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (to, from) => {
    if ((to.name === 'patient-detail' || to.name === 'patient-report-center' || to.name === 'report-ocr-review') && to.path === from.path) return false
    return { top: 0 }
  },
})

installRouteLoadRecovery(router)

router.beforeEach(async (to) => {
  document.title = `${String(to.meta.title ?? '管理后台')} · 华阳羿鑫`

  if (to.name === 'login') {
    if (getAccessToken()) return '/patients'
    return true
  }

  if (!to.matched.some((record) => record.meta.requiresAuth)) return true

  const session = useSessionStore()
  try {
    if (!getAccessToken()) await refreshAccessToken()
    await session.initialize()
  } catch {
    return { path: '/login', query: { redirect: to.fullPath } }
  }

  const permission = String(to.meta.permission ?? '')
  if (permission && !session.hasPermission(permission)) {
    return { path: '/forbidden', query: { from: to.fullPath } }
  }

  const permissionsAny = Array.isArray(to.meta.permissionsAny) ? to.meta.permissionsAny : []
  if (permissionsAny.length && !permissionsAny.some((item) => session.hasPermission(String(item)))) {
    return { path: '/forbidden', query: { from: to.fullPath } }
  }

  return true
})

export default router
